import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersQuery, InventoryKindBO } from '@skylab-shared/business';
import { EventBusBase } from '@skylab-shared/core';
import { CalculationMethod, InventoryDocumentKind, InventoryKindModel, LabeledEnumValue, LabeledEnumValueConverter } from '@skylab-shared/dataModel';

import { InventoryKindRepository, InventoryKindRepositoryBase } from '@skylab-angular-shared/business';
import { CommandBus, EnumTranslator, EnumTranslatorBase, EventBus, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, FocusByDirective, FormContainerComponent, SelectValueAccessorDirective, TextValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DictionaryDialogComponentBase, DictionaryDialogData, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface InventoryKindDialogResult {
    id: number;
    version: string;
}

interface InventoryKindDialogViewModel extends DictionaryDialogViewModelBase<InventoryKindBO> {
    calculationMethods: LabeledEnumValue[];
    calculationMethodsConverter: LabeledEnumValueConverter<CalculationMethod>;
    documentName: string;
    focusReady: boolean;
    useCompanyTransactionLocation: { value: boolean; };
    isTransactionLocationVisible: () => boolean;
    isDefaultCalculationMethodVisible: boolean;
}

@Component({
    selector: 'skl-inventory-kind-dialog',
    templateUrl: './inventory-kind-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, FocusByDirective, TextValueAccessorDirective, FormsModule, ValidateDirective, SelectValueAccessorDirective, BooleanValueAccessorDirective, ButtonComponent, TranslatePipe]
})
export class InventoryKindDialogComponent extends DictionaryDialogComponentBase
<
InventoryKindModel,
InventoryKindBO,
InventoryKindRepositoryBase,
InventoryKindDialogViewModel,
DictionaryDialogData<string>
> {
    public constructor(
        @Inject(InventoryKindRepository) isnventoryKindRepositoryBase: InventoryKindRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        toastService: ToastService,
        commandBus: CommandBus,

        dialogService: DialogInsService,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase
    ) {
        super(isnventoryKindRepositoryBase, toastService, l10n, weaver, rootContext, commandBus, eventBus, dialogService);
    }

    protected override getInitialState(): InventoryKindDialogViewModel {
        return {
            ...super.getInitialState(),
            calculationMethods: this.enumTranslator.getLabeledEnumValueArray(CalculationMethod).filter(le => ![CalculationMethod.Manual, CalculationMethod.Margin].includes(le.value as CalculationMethod)),
            calculationMethodsConverter: new LabeledEnumValueConverter(this.enumTranslator, CalculationMethod),
            documentName: '',
        };
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([
            super.preloadDataAsync(),
            this.localContext.preload(CompanyParametersQuery)
        ]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();

        const self = this;
        this.viewModel.useCompanyTransactionLocation = {
            get value() {
                return !self.viewModel.bo.model.useCustomTransactionLocation;
            },
            set value(value) {
                self.viewModel.bo.model.useCustomTransactionLocation = !value;
            }
        };
        this.viewModel.documentName = this.enumTranslator.getLabeledEnumValue(InventoryDocumentKind, this.viewModel.bo.model.name).label;
        this.viewModel.isTransactionLocationVisible = () => this.viewModel.bo.isGoodsIssueType;

        const companyParameters = this.localContext.getPreloaded(CompanyParametersQuery!);
        const vatPayer = companyParameters?.vatPayer;
        this.viewModel.isDefaultCalculationMethodVisible = (this.viewModel.bo.model.name === InventoryDocumentKind.GoodsReceipt) || (!!vatPayer && (this.viewModel.bo.model.name !== InventoryDocumentKind.Stocktaking));
    }

    protected override get dictionaryToLoadProperties(): { id: number | null, [key: string]: string | number | null } | null {
        return this.data?.id ? { id: null, name: this.data.id } : null;
    }
}
